import React, { useState, useEffect, useCallback } from 'react';
import { DndContext, DragEndEvent, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { DroppableCell } from './DroppableCell';
import { DraggablePostIt } from './DraggablePostIt';
import { MoodEntry, User } from '../../types';
import api from '../../services/api';
import { MeetingFeedbackModal } from '../MeetingFeedbackModal';

interface MoodBoardProps {
    sessionId: number;
    currentUser: User;
    onConfirm: () => void;
}

export const MoodBoard: React.FC<MoodBoardProps> = ({ sessionId, currentUser, onConfirm }) => {
    const [entries, setEntries] = useState<MoodEntry[]>([]);
    const [ws, setWs] = useState<WebSocket | null>(null);
    const [isLocked, setIsLocked] = useState(false);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    // Fetch initial state
    useEffect(() => {
        const fetchEntries = async () => {
            try {
                // Fetch entries for the session
                const res = await api.get(`/sessions/${sessionId}/mood-entries`);
                setEntries(res.data);

                // Check if current user is locked
                const myEntry = res.data.find((e: MoodEntry) => e.user_id === currentUser.id);
                if (myEntry?.is_locked) {
                    setIsLocked(true);
                }
            } catch (err) {
                console.error("Failed to fetch entries", err);
            }
        };
        fetchEntries();
    }, [sessionId, currentUser.id]);

    // WebSocket connection
    useEffect(() => {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

        let wsUrl;
        if (window.location.hostname === 'localhost') {
            // Local development
            wsUrl = `${protocol}//${window.location.hostname}:8000/ws/${sessionId}`;
        } else {
            // Production (behind ingress)
            wsUrl = `${protocol}//${window.location.host}/ws/${sessionId}`;
        }

        console.log(`[MoodBoard] Connecting to WebSocket: ${wsUrl} with sessionId: ${sessionId}`);
        const socket = new WebSocket(wsUrl);

        socket.onopen = () => {
            console.log('[MoodBoard] Connected to WebSocket');
        };

        socket.onerror = (error) => {
            console.error('[MoodBoard] WebSocket Error:', error);
        };

        socket.onmessage = (event) => {
            const data = JSON.parse(event.data);
            if (data.type === 'MOOD_UPDATE') {
                setEntries((prev) => {
                    const index = prev.findIndex(e => e.user_id === data.entry.user_id);
                    // Check if update is for current user and if it affects locking? 
                    // Usually lock is private action but status might be shared. 
                    // Ideally we fetch lock status from server on logic change.

                    if (index >= 0) {
                        const newEntries = [...prev];
                        newEntries[index] = data.entry;
                        return newEntries;
                    }
                    return [...prev, data.entry];
                });
            }
        };

        setWs(socket);

        return () => {
            socket.close();
        };
    }, [sessionId]);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        if (isLocked) return;

        const { active, over } = event;

        if (over) {
            const { x, y } = over.data.current as { x: number; y: number };

            // Update local state optimistically
            const newEntry: MoodEntry = {
                id: Date.now(), // Temp ID
                session_id: sessionId,
                user_id: currentUser.id,
                x_coord: x,
                y_coord: y,
                emotion: "Updated",
                is_locked: false,
                user: currentUser
            };

            setEntries((prev) => {
                const index = prev.findIndex(e => e.user_id === currentUser.id);
                if (index >= 0) {
                    const newEntries = [...prev];
                    newEntries[index] = newEntry;
                    return newEntries;
                }
                return [...prev, newEntry];
            });

            // Send to WebSocket
            if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({
                    type: 'MOOD_UPDATE',
                    entry: newEntry
                }));
            }
        }
    }, [currentUser, sessionId, ws, isLocked]);

    const handleConfirm = () => {
        setShowFeedbackModal(true);
    };

    const handleFeedbackSuccess = async () => {
        try {
            await api.post(`/sessions/${sessionId}/lock-entry`);
            setIsLocked(true);
            onConfirm();
        } catch (error) {
            console.error("Failed to lock entry", error);
        }
    };

    // Render Grid
    const renderGrid = () => {
        const grid = [];
        for (let y = 7; y >= 0; y--) {
            for (let x = 0; x < 8; x++) {
                const entry = entries.find(e => e.x_coord === x && e.y_coord === y);
                grid.push(
                    <div key={`${x}-${y}`} className="w-full h-full aspect-square">
                        <DroppableCell x={x} y={y}>
                            {entry && (
                                <DraggablePostIt
                                    id={`postit-${entry.user_id}`}
                                    content={entry.user?.full_name || 'User'}
                                    color={entry.user?.color_preference}
                                />
                            )}
                        </DroppableCell>
                    </div>
                );
            }
        }
        return grid;
    };

    const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);

    useEffect(() => {
        if (isLocked) {
            setShowSuccessOverlay(true);
            const timer = setTimeout(() => {
                setShowSuccessOverlay(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [isLocked]);

    // Check if current user has placed a post-it
    const hasPlaced = entries.some(e => e.user_id === currentUser.id);

    return (
        <div className="flex flex-col items-center gap-4 w-full max-w-5xl mx-auto p-4">
            <div className={`relative w-full aspect-square max-w-[800px] ${isLocked ? 'pointer-events-none' : ''}`}>
                {/* Y-Axis Label */}
                <div className="absolute -left-12 top-0 bottom-0 flex items-center justify-center z-10">
                    <span className="text-lg font-bold -rotate-90 tracking-widest text-gray-600 whitespace-nowrap">ENERGY</span>
                </div>

                {/* X-Axis Label */}
                <div className="absolute left-0 right-0 -bottom-8 flex items-center justify-center">
                    <span className="text-lg font-bold tracking-widest text-gray-600">PLEASANTNESS</span>
                </div>

                <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                    <div className="grid grid-cols-8 grid-rows-8 w-full h-full border-2 border-border-base bg-surface shadow-2xl rounded-lg overflow-hidden">
                        {renderGrid()}
                    </div>

                    {!hasPlaced && !isLocked && (
                        <div className="absolute -bottom-24 left-1/2 -translate-x-1/2 p-4 bg-gray-100 rounded-lg shadow-inner flex gap-4 items-center">
                            <span className="text-sm text-gray-500">Drag your note to the board:</span>
                            <DraggablePostIt
                                id={`postit-${currentUser.id}`}
                                content={currentUser.full_name}
                                color={currentUser.color_preference}
                            />
                        </div>
                    )}
                </DndContext>

                {showSuccessOverlay && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/10 z-10 transition-opacity duration-500">
                        <div className="bg-white/90 px-6 py-4 rounded-xl shadow-2xl border border-gray-200 animate-bounce">
                            <span className="text-xl font-bold text-green-600">Locked & Submitted</span>
                        </div>
                    </div>
                )}
            </div>

            {hasPlaced && !isLocked && (
                <button
                    onClick={handleConfirm}
                    className="mt-8 px-8 py-3 bg-black text-white text-lg font-bold rounded-full hover:bg-gray-800 transition-transform hover:scale-105 shadow-lg"
                >
                    Confirm Placement
                </button>
            )}
            {isLocked && (
                <div className="mt-8 px-8 py-3 bg-green-100 text-green-800 text-lg font-bold rounded-full border border-green-200">
                    Submission Confirmed
                </div>
            )}

            <MeetingFeedbackModal
                isOpen={showFeedbackModal}
                onClose={() => setShowFeedbackModal(false)}
                sessionId={sessionId}
                userId={currentUser.id}
                onSuccess={handleFeedbackSuccess}
            />
        </div>
    );
};
